import React, { useEffect, createElement, Component } from 'react';
import { Star, Clock, Frown, Repeat, Brain, CheckCircle2, Quote } from 'lucide-react';
import { CTAButton } from './components/CTAButton';
import { AccordionItem } from './components/Accordion';
import { Countdown } from './components/Countdown';
import { PurchasePopup } from './components/PurchasePopup';
export function App() {
  // Meta Pixel — PageView
  useEffect(() => {
    const PIXEL_ID = '2782582688765590';
    const w = window as any;
    if (!w.fbq) {
      const n: any = w.fbq = function (...args: any[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      };
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const s = document.createElement('script');
      s.async = true;
      s.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const first = document.getElementsByTagName('script')[0];
      first?.parentNode?.insertBefore(s, first);
    }
    w.fbq('init', PIXEL_ID);
    w.fbq('track', 'PageView');
    // noscript fallback
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`;
    document.body.appendChild(img);
    return () => {
      if (img.parentNode) document.body.removeChild(img);
    };
  }, []);
  return <div className="min-h-screen bg-[#EAF7F1] font-sans text-gray-800 selection:bg-emerald-200" data-id="element-0">
      <PurchasePopup data-id="element-1" />

      {/* HERO SECTION */}
      <section className="pt-12 pb-16 px-4 max-w-4xl mx-auto text-center" data-id="element-2">
        <h1 className="text-3xl md:text-5xl font-extrabold text-teal-700 uppercase mb-8 leading-tight tracking-tight" data-id="element-3">
          Dejá de improvisar tus clases de gimnasia postural
        </h1>

        <div className="relative w-full max-w-2xl mx-auto mb-8" data-id="element-4">
          <a href="https://checkout.ebookbtnlines.shop/cart/45530178650159:1" className="block" data-id="element-5">
            <img src="https://cdn.magicpatterns.com/uploads/9UqNhFhe5bVffpVJzg5Rxu/GP_SIN_FONDO.png" alt="Gimnasia Postural Resuelta Mockup" className="w-full h-auto drop-shadow-2xl hover:scale-[1.02] transition-transform duration-300" data-id="element-6" />
          </a>
        </div>

        <h2 className="text-lg md:text-xl text-gray-700 font-medium mb-6 max-w-2xl mx-auto leading-relaxed" data-id="element-7">
          Todo lo que necesitás para crear clases de gimnasia postural más
          organizadas, profesionales y seguras.
        </h2>

        <div className="flex items-center justify-center gap-1 mb-8" data-id="element-8">
          {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-9" />)}
          <span className="ml-2 font-bold text-gray-800" data-id="element-10">4.9/5 estrellas</span>
        </div>

        <CTAButton data-id="element-11" />

        <p className="mt-4 text-teal-700 font-bold uppercase tracking-widest text-sm" data-id="element-12">
          Oferta especial solo por hoy
        </p>
      </section>

      {/* MODULE 1: PAIN POINTS */}
      <section className="py-16 px-4 bg-white" data-id="element-13">
        <div className="max-w-4xl mx-auto" data-id="element-14">
          <div className="text-center mb-12" data-id="element-15">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-16">
              ¿Te suena familiar esta situación?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-17">
              Sabemos lo frustrante que es llegar a una clase sin tener claro
              cómo trabajar cada caso, improvisando y utilizando las mismas
              herramientas una y otra vez.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-18">
            <PainCard icon={<Clock className="w-8 h-8 text-teal-600" data-id="element-20" />} title="Pérdida de Tiempo" desc="Pasás horas buscando ejercicios, correcciones y adaptaciones en Instagram, YouTube o Google para distintas alteraciones posturales en lugar de descansar, disfrutar tu tiempo libre o dedicarte a hacer crecer tu profesión." data-id="element-19" />
            <PainCard icon={<Frown className="w-8 h-8 text-teal-600" data-id="element-22" />} title="Estrés e Improvisación" desc="Llega un alumno con escoliosis, hiperlordosis o dolor cervical y no siempre tenés claro qué ejercicios convienen, cuáles evitar y cómo adaptar la clase correctamente." data-id="element-21" />
            <PainCard icon={<Repeat className="w-8 h-8 text-teal-600" data-id="element-24" />} title="Falta de Recursos y Variedad" desc="Terminás utilizando siempre los mismos ejercicios y secuencias porque no contás con suficientes herramientas para abordar distintos perfiles y necesidades." data-id="element-23" />
            <PainCard icon={<Brain className="w-8 h-8 text-teal-600" data-id="element-26" />} title="Agotamiento Mental" desc="Más allá de enseñar, tenés que pensar constantemente cómo adaptar cada ejercicio, cómo progresar y cómo responder a diferentes problemáticas posturales." data-id="element-25" />
          </div>
        </div>
      </section>

      {/* MODULE 2: BENEFITS */}
      <section className="py-16 px-4 bg-[#E3F0F7]" data-id="element-27">
        <div className="max-w-4xl mx-auto" data-id="element-28">
          <h2 className="text-2xl md:text-4xl font-bold text-center text-gray-900 mb-12" data-id="element-29">
            Lo que vas a lograr con Gimnasia Postural Resuelta
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-30">
            <BenefitCard title="Ahorrá Horas de Planificación" desc="Dejá de pasar horas buscando ejercicios y adaptaciones para cada caso. Vas a tener herramientas listas para aplicar desde el primer día." data-id="element-31" />
            <BenefitCard title="Clases Más Profesionales y Estructuradas" desc="Entrá a cada clase sabiendo exactamente qué hacer y cómo trabajar con cada alumno según sus necesidades." data-id="element-32" />
            <BenefitCard title="Menos Estrés, Más Disfrute" desc="Dejá atrás la improvisación y volvé a disfrutar enseñar con un sistema claro y organizado." data-id="element-33" />
          </div>

          <div className="mt-12 text-center" data-id="element-34">
            <CTAButton data-id="element-35" />
          </div>
        </div>
      </section>

      {/* MODULE 3: CURRICULUM */}
      <section className="py-16 px-4 bg-white" data-id="element-36">
        <div className="max-w-3xl mx-auto" data-id="element-37">
          <div className="text-center mb-10" data-id="element-38">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-39">
              GIMNASIA POSTURAL RESUELTA
            </h2>
            <p className="text-lg text-teal-700 font-medium" data-id="element-40">
              📚 Formación completa para profesionales de la gimnasia postural
            </p>
          </div>

          <AccordionItem title="TOMO 1 — Fundamentos de la postura" isOpen data-id="element-41">
            <ul className="space-y-2" data-id="element-42">
              <ListItem text="Anatomía postural y cadenas musculares." data-id="element-43" />
              <ListItem text="Cadenas anterior, posterior y cruzadas." data-id="element-44" />
              <ListItem text="Ejes y planos corporales." data-id="element-45" />
              <ListItem text="Test visual." data-id="element-46" />
              <ListItem text="Fotografías comparativas." data-id="element-47" />
              <ListItem text="Evaluación con plomada." data-id="element-48" />
              <ListItem text="Apps de análisis postural." data-id="element-49" />
            </ul>
          </AccordionItem>

          <AccordionItem title="TOMO 2 — Desalineaciones y patologías más comunes" data-id="element-50">
            <ul className="space-y-2" data-id="element-51">
              <ListItem text="Hipercifosis." data-id="element-52" />
              <ListItem text="Hiperlordosis." data-id="element-53" />
              <ListItem text="Escoliosis." data-id="element-54" />
              <ListItem text="Anteproyección cervical." data-id="element-55" />
              <ListItem text="Báscula pélvica." data-id="element-56" />
              <ListItem text="Pie plano." data-id="element-57" />
              <ListItem text="Pie cavo." data-id="element-58" />
              <ListItem text="Genu valgo." data-id="element-59" />
              <ListItem text="Genu varo." data-id="element-60" />
              <ListItem text="Causas frecuentes." data-id="element-61" />
              <ListItem text="Abordaje desde el ejercicio." data-id="element-62" />
            </ul>
          </AccordionItem>

          <AccordionItem title="TOMO 3 — Gimnasia postural según población" data-id="element-63">
            <ul className="space-y-2" data-id="element-64">
              <ListItem text="Niños y adolescentes." data-id="element-65" />
              <ListItem text="Oficinistas y sedentarios." data-id="element-66" />
              <ListItem text="Adultos mayores." data-id="element-67" />
              <ListItem text="Embarazadas." data-id="element-68" />
              <ListItem text="Deportistas." data-id="element-69" />
              <ListItem text="Adaptaciones según población." data-id="element-70" />
            </ul>
          </AccordionItem>

          <AccordionItem title="TOMO 4 — Rutinas y ejercicios correctivos por zona" data-id="element-71">
            <ul className="space-y-2" data-id="element-72">
              <ListItem text="Cervical." data-id="element-73" />
              <ListItem text="Dorsal y escápulas." data-id="element-74" />
              <ListItem text="Lumbar." data-id="element-75" />
              <ListItem text="Cadera." data-id="element-76" />
              <ListItem text="Miembros inferiores." data-id="element-77" />
              <ListItem text="Progresiones." data-id="element-78" />
              <ListItem text="Uso de materiales." data-id="element-79" />
              <ListItem text="Clases individuales y grupales." data-id="element-80" />
            </ul>
          </AccordionItem>

          <AccordionItem title="TOMO 5 — Reeducación postural y técnicas complementarias" data-id="element-81">
            <ul className="space-y-2" data-id="element-82">
              <ListItem text="Introducción a RPG." data-id="element-83" />
              <ListItem text="Conceptos de Mézières." data-id="element-84" />
              <ListItem text="Pilates postural." data-id="element-85" />
              <ListItem text="Trabajo respiratorio." data-id="element-86" />
              <ListItem text="Conciencia corporal." data-id="element-87" />
            </ul>
          </AccordionItem>

          <AccordionItem title="TOMO 6 — Programación, seguimiento y negocio" data-id="element-88">
            <ul className="space-y-2" data-id="element-89">
              <ListItem text="Clases grupales e individuales." data-id="element-90" />
              <ListItem text="Evaluación inicial." data-id="element-91" />
              <ListItem text="Reevaluación a 30, 60 y 90 días." data-id="element-92" />
              <ListItem text="Registro del antes y después." data-id="element-93" />
              <ListItem text="Armado de paquetes." data-id="element-94" />
              <ListItem text="Fidelización." data-id="element-95" />
              <ListItem text="Trabajo interdisciplinario." data-id="element-96" />
            </ul>
          </AccordionItem>
        </div>
      </section>

      {/* MODULE 4: TESTIMONIALS */}
      <section className="py-16 px-4 bg-[#EAF7F1]" data-id="element-97">
        <div className="max-w-5xl mx-auto" data-id="element-98">
          <div className="text-center mb-12" data-id="element-99">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-100">
              Profesionales que ya llevaron sus clases al siguiente nivel
            </h2>
            <p className="text-lg text-gray-600" data-id="element-101">
              Más de 500 profesionales de Argentina ya utilizan nuestro sistema.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-102">
            <TestimonialCard name="Camila, 31 años" city="Córdoba" quote="Por primera vez siento que tengo un criterio claro para adaptar ejercicios según cada alumno." data-id="element-103" />
            <TestimonialCard name="Florencia, 38 años" city="Rosario" quote="Antes improvisaba muchísimo. Hoy entro a cada clase sabiendo exactamente qué hacer." data-id="element-104" />
            <TestimonialCard name="Luciana, 35 años" city="Buenos Aires" quote="Mis alumnos empezaron a notar mejoras mucho más rápido y eso hizo crecer muchísimo las recomendaciones." data-id="element-105" />
            <TestimonialCard name="Valeria, 42 años" city="Mendoza" quote="El módulo de desalineaciones me dio herramientas que nunca había aprendido en otras formaciones." data-id="element-106" />
            <TestimonialCard name="Mariana, 29 años" city="La Plata" quote="Me ayudó muchísimo a sentirme más segura trabajando con alumnos con molestias y patologías frecuentes." data-id="element-107" />
          </div>
        </div>
      </section>

      {/* MODULE 5: BONUSES */}
      <section className="py-16 px-4 bg-white" data-id="element-108">
        <div className="max-w-3xl mx-auto" data-id="element-109">
          <div className="text-center mb-10" data-id="element-110">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-111">
              Oferta por tiempo limitado
            </h2>
            <p className="text-xl text-teal-700 font-bold uppercase tracking-wide" data-id="element-112">
              Llevate estos BONOS GRATIS hoy
            </p>
          </div>

          <AccordionItem title="🎁 Bono 1 — Fichas de evaluación postural imprimibles" previewImage="https://cdn.magicpatterns.com/uploads/weykoiK3okBZRfGJSNtmb8/WhatsApp_Image_2026-06-28_at_19.15.42.jpg" isOpen data-id="element-113">
            <ul className="space-y-2" data-id="element-114">
              <ListItem text="Evaluación inicial." data-id="element-115" />
              <ListItem text="Reevaluación a 30, 60 y 90 días." data-id="element-116" />
              <ListItem text="Registro de evolución." data-id="element-117" />
            </ul>
          </AccordionItem>

          <AccordionItem title="🎁 Bono 2 — Biblioteca de ejercicios correctivos" previewImage="https://cdn.magicpatterns.com/uploads/sMRCarbK4Xv1pS9ggcsgEC/WhatsApp_Image_2026-06-28_at_19.15.42_(1).jpg" data-id="element-118">
            <ul className="space-y-2" data-id="element-119">
              <ListItem text="Ejercicios organizados por región corporal." data-id="element-120" />
              <ListItem text="Objetivos." data-id="element-121" />
              <ListItem text="Progresiones." data-id="element-122" />
              <ListItem text="Errores frecuentes." data-id="element-123" />
            </ul>
          </AccordionItem>

          <AccordionItem title="🎁 Bono 3 — Programas listos para aplicar" previewImage="https://cdn.magicpatterns.com/uploads/pEBVSjLwZxMxAYPLuKegvT/WhatsApp_Image_2026-06-28_at_19.15.42_(2).jpg" data-id="element-124">
            <ul className="space-y-2" data-id="element-125">
              <ListItem text="Programas para oficinistas." data-id="element-126" />
              <ListItem text="Adultos mayores." data-id="element-127" />
              <ListItem text="Embarazadas." data-id="element-128" />
              <ListItem text="Deportistas." data-id="element-129" />
            </ul>
          </AccordionItem>

          <AccordionItem title="🎁 Bono 4 — Pack de clases grupales listas" previewImage="https://cdn.magicpatterns.com/uploads/qSDF41B16xe4XZJ5SAdX45/WhatsApp_Image_2026-06-28_at_19.15.43.jpg" data-id="element-130">
            <ul className="space-y-2" data-id="element-131">
              <ListItem text="Clases completas listas para implementar." data-id="element-132" />
              <ListItem text="Objetivos." data-id="element-133" />
              <ListItem text="Variantes." data-id="element-134" />
              <ListItem text="Progresiones." data-id="element-135" />
            </ul>
          </AccordionItem>

          <AccordionItem title="🎁 Bono 5 — Cuestionario de detección rápida" previewImage="https://cdn.magicpatterns.com/uploads/f35yhNXitMB6TziUcVALS6/WhatsApp_Image_2026-06-28_at_19.15.43_(1).jpg" data-id="element-136">
            <ul className="space-y-2" data-id="element-137">
              <ListItem text="Dolor cervical." data-id="element-138" />
              <ListItem text="Dolor lumbar." data-id="element-139" />
              <ListItem text="Sedentarismo." data-id="element-140" />
              <ListItem text="Hábitos laborales." data-id="element-141" />
              <ListItem text="Factores de riesgo postural." data-id="element-142" />
            </ul>
          </AccordionItem>
        </div>
      </section>

      {/* MODULE 6: OFFER */}
      <section className="py-20 px-4 bg-teal-700" data-id="element-143">
        <div className="max-w-xl mx-auto text-center bg-white p-8 md:p-12 rounded-[2rem] shadow-2xl ring-4 ring-teal-500/30 border border-emerald-100 relative overflow-hidden" data-id="element-144">
          {/* corner accent */}
          <div className="absolute -top-16 -right-16 w-40 h-40 bg-emerald-100 rounded-full opacity-60" data-id="element-145" />
          <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-teal-100 rounded-full opacity-50" data-id="element-146" />

          <div className="relative" data-id="element-147">
            <span className="inline-block bg-teal-700 text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6 animate-pulse" data-id="element-148">
              🔥 Oferta por tiempo limitado
            </span>

            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3" data-id="element-149">
              Esto es todo lo que te llevás
            </h2>
            <p className="text-gray-600 max-w-md mx-auto mb-2 leading-relaxed" data-id="element-150">
              Acceso inmediato y de por vida a{' '}
              <span className="font-semibold text-teal-700" data-id="element-151">
                Gimnasia Postural Resuelta
              </span>{' '}
              más todos los bonos de regalo.
            </p>

            <Countdown data-id="element-152" />

            <div className="my-8 bg-emerald-50 rounded-2xl py-6 px-4 border border-emerald-100" data-id="element-153">
              <p className="text-gray-500 text-lg line-through decoration-gray-400 decoration-2 mb-1" data-id="element-154">
                Precio regular: $49.990
              </p>
              <p className="text-5xl md:text-6xl font-black text-teal-700 leading-none" data-id="element-155">
                $19.990{' '}
                <span className="text-xl text-gray-500 font-medium" data-id="element-156">ARS</span>
              </p>
              <span className="inline-block mt-3 bg-teal-700 text-white text-sm font-bold px-4 py-1.5 rounded-full" data-id="element-157">
                Ahorrás $30.000 hoy
              </span>
            </div>

            <CTAButton className="text-lg py-5 shadow-teal-700/30" data-id="element-158" />

            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500" data-id="element-159">
              <CheckCircle2 className="w-4 h-4 text-teal-600" data-id="element-160" />
              <span data-id="element-161">Pago seguro y acceso inmediato</span>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 text-center text-gray-500 text-sm bg-white" data-id="element-162">
        <p data-id="element-163">
          © {new Date().getFullYear()} Gimnasia Postural Resuelta. Todos los
          derechos reservados.
        </p>
      </footer>
    </div>;
}
// Helper Components
function PainCard({
  icon,
  title,
  desc
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return <div className="bg-emerald-50/60 p-8 rounded-3xl border border-emerald-100 hover:shadow-lg transition-shadow" data-id="element-164">
      <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-6" data-id="element-165">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-166">{title}</h3>
      <p className="text-gray-600 leading-relaxed" data-id="element-167">{desc}</p>
    </div>;
}
function BenefitCard({
  title,
  desc
}: {
  title: string;
  desc: string;
}) {
  return <div className="bg-white p-8 rounded-3xl shadow-sm border border-emerald-50 flex gap-4 hover:shadow-md transition-shadow" data-id="element-168">
      <div className="flex-shrink-0 mt-1" data-id="element-169">
        <CheckCircle2 className="w-6 h-6 text-teal-600" data-id="element-170" />
      </div>
      <div data-id="element-171">
        <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-172">{title}</h3>
        <p className="text-gray-600 leading-relaxed" data-id="element-173">{desc}</p>
      </div>
    </div>;
}
function ListItem({
  text
}: {
  text: string;
}) {
  return <li className="flex items-start gap-3" data-id="element-174">
      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-teal-500 flex-shrink-0" data-id="element-175" />
      <span data-id="element-176">{text}</span>
    </li>;
}
function TestimonialCard({
  name,
  city,
  quote
}: {
  name: string;
  city: string;
  quote: string;
}) {
  return <div className="bg-white p-8 rounded-3xl shadow-sm border border-emerald-50 relative" data-id="element-177">
      <Quote className="absolute top-6 right-6 w-8 h-8 text-emerald-100" data-id="element-178" />
      <div className="flex items-center gap-4 mb-6" data-id="element-179">
        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center text-teal-700 font-bold text-xl" data-id="element-180">
          {name.charAt(0)}
        </div>
        <div data-id="element-181">
          <h4 className="font-bold text-gray-900" data-id="element-182">{name}</h4>
          <p className="text-sm text-gray-500" data-id="element-183">{city}</p>
        </div>
      </div>
      <p className="text-gray-700 italic leading-relaxed relative z-10" data-id="element-184">
        "{quote}"
      </p>
    </div>;
}